import { rem } from 'polished'
import { useMemo } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import styled from 'styled-components'

import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import Group from 'components/utils/Group'
import config from 'constants/config'
import { useAppSelector } from 'hooks/reduxHooks'
import useSwitchRegion from 'hooks/useSwitchRegion'
import TextButton from 'LuxKit/Button/TextButton'
import Flag from 'LuxKit/Flag'
import BodyText from 'LuxKit/Typography/BodyText'
import getLoyaltyAccountRegion from 'loyalty/selectors/getLoyaltyAccountRegion'
import getLoyaltyTierDisplayName from 'loyalty/selectors/getLoyaltyTierDisplayName'

const StandaloneCard = styled.div`
  width: 100%;
  padding: ${rem(24)};
  border-radius: ${props => props.theme.borderRadius.L};
  color: ${props => props.theme.palette.neutral.default.one};
  background: ${props => props.theme.palette.neutral.surface.normalBackground};
  box-shadow: ${props => props.theme.shadow.bottom.medium};
`

interface Props {
  programName: string
  userTier: App.LoyaltyTier
  /** Wrap in a standalone card for the top-up page hero panel. */
  standalone?: boolean
}

function LoyaltySwitchMembershipRegionPanel({ programName, userTier, standalone = false }: Props) {
  const intl = useIntl()
  const loyaltyAccountRegion = useAppSelector(getLoyaltyAccountRegion)
  const loyaltyCurrencyLabel = useMemo(
    () => config.regions.find(region => region.code === loyaltyAccountRegion)?.currencyCode,
    [loyaltyAccountRegion],
  )
  const [switchRegionPath, handleSwitchRegion] = useSwitchRegion(loyaltyAccountRegion)
  const tierName = getLoyaltyTierDisplayName(userTier)

  if (!loyaltyCurrencyLabel) {
    return null
  }

  const switchRegionPromptMobile = intl.formatMessage({
    id: 'Xl0F77pN-5M4frww2waE',
    defaultMessage: 'Switch to {currency} to view your {programName} Points\u00A0balance and access your {tierName} benefits',
    description: 'Prompt when browsing outside the loyalty account region on mobile for spend points, top up, and lounge pass pages',
  }, {
    currency: loyaltyCurrencyLabel,
    programName,
    tierName,
  })

  const content = <Group direction="vertical" gap={16} horizontalAlign="center">
    <BodyText variant="medium" align="center">
      <CSSBreakpoint max="mobile">
        {switchRegionPromptMobile}
      </CSSBreakpoint>
      <CSSBreakpoint min="tablet">
        <FormattedMessage
          id="gQ3pWOsQKjM-FReaOLYO"
          defaultMessage="Switch to {currency} to view your {programName} Points balance<lineBreak></lineBreak>and access your {tierName} benefits"
          description="Prompt when browsing outside the loyalty account region on tablet and desktop for spend points, top up, and lounge pass pages"
          values={{
            currency: loyaltyCurrencyLabel,
            programName,
            tierName,
            lineBreak: () => <br key="switch-region-line-break" />,
          }}
        />
      </CSSBreakpoint>
    </BodyText>
    <TextButton
      kind="secondary"
      variant="dark"
      size="large"
      shape="square"
      fit="full-width"
      startIcon={loyaltyAccountRegion ? <Flag countryCode={loyaltyAccountRegion} /> : undefined}
      href={switchRegionPath}
      onClick={handleSwitchRegion}
    >
      <FormattedMessage
        id="N_ufYqvQN3hfk5C-ia_Q"
        defaultMessage="Switch to {currency}"
        description="Link to switch to the user's loyalty account region"
        values={{ currency: loyaltyCurrencyLabel }}
      />
    </TextButton>
  </Group>

  if (standalone) {
    return <StandaloneCard>{content}</StandaloneCard>
  }

  return content
}

export default LoyaltySwitchMembershipRegionPanel
